import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { GraduationCap, TrendingUp, ArrowRight } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";

/**
 * Public SEO-friendly gallery of the most-viewed Professor Compare verdicts.
 *
 * Every card is a permalink to a cached LLM verdict → zero LLM cost to
 * serve, indexable by Google, and driving organic backlinks for phrases
 * like "Oxford PPE vs LSE PPE" that no ranking site currently owns.
 */
export default function TrendingVerdicts() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/compare/verdicts/trending", {
          params: { limit: 24 },
        });
        if (!cancelled) setItems(data.verdicts || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const buildHref = (v) => {
    const prefix = v.kind === "degree" ? "d" : "u";
    const params = new URLSearchParams();
    if (v.kind === "degree") params.set("tab", "degrees");
    (v.ids || []).slice(0, 6).forEach((id, i) => params.set(`${prefix}${i + 1}`, id));
    params.set("verdict_id", v.verdict_id);
    return `/compare?${params.toString()}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title="Trending university comparisons · WhichUniversityAdvisor"
        description="The most-viewed university and degree comparisons on WhichUniversityAdvisor — Oxford vs Cambridge, MIT vs Stanford, LSE vs UCL and more. Each opens a full editorial verdict from The Professor with real numbers."
        image="/og/uk-visa-friendly-degrees.png"
        path="/compare/trending"
      />

      <div className="flex items-start gap-3">
        <TrendingUp className="h-7 w-7 text-[#4C1D95] mt-2" strokeWidth={2} />
        <div>
          <div className="label-uppercase">Trending comparisons</div>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-2">
            What everyone else is comparing.
          </h1>
          <p className="mt-3 text-stone-600 max-w-2xl">
            Every card opens a fully editorial verdict from The Professor —
            written once, cached forever, free to read and share.
          </p>
        </div>
      </div>

      {items === null && (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 rounded-lg bg-stone-100 animate-pulse" />
          ))}
        </div>
      )}

      {items && items.length === 0 && (
        <div className="mt-16 text-center text-stone-600">
          <p className="text-lg">
            No trending comparisons yet. Be the first — go to{" "}
            <Link to="/compare" className="text-[#4C1D95] underline">
              Compare
            </Link>{" "}
            and Ask The Professor.
          </p>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="trending-grid">
          {items.map((v) => (
            <Link
              key={v.verdict_id}
              to={buildHref(v)}
              data-testid={`trending-card-${v.verdict_id}`}
              className="group block p-5 rounded-lg border border-stone-200 bg-white hover:border-[#4C1D95] hover:shadow-md transition"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <GraduationCap className="h-4 w-4 text-[#4C1D95]" />
                  <span className="text-[10px] font-underground-sc tracking-wider text-[#4C1D95]">
                    The Professor · {v.kind === "degree" ? "degree" : "university"}
                  </span>
                </div>
                <span className="text-[10px] font-mono text-stone-400">
                  {v.view_count} view{v.view_count === 1 ? "" : "s"}
                </span>
              </div>
              <div className="mt-2 font-editorial text-lg leading-tight text-stone-900 line-clamp-2">
                {v.title || (v.items || []).join(" vs ")}
              </div>
              <p className="mt-3 text-sm text-stone-600 line-clamp-3">
                {v.summary_snippet}…
              </p>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-underground-sc tracking-wider text-[#4C1D95] group-hover:gap-2 transition-all">
                Read the verdict <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
